import type { ExtractionAlgorithm } from "../../lib/paletteExtraction";

export interface ExtractionSettings {
  extractionK: number;
  extractionAlgo: ExtractionAlgorithm;
  brandSafeEnabled: boolean;
  removeOutliers: boolean;
  outlierThreshold: number;
  contrastBias: boolean;
  targetBackground: string;
  minLightness: number | undefined;
  maxLightness: number | undefined;
  minSaturation: number | undefined;
}

interface ExtractionSettingsPanelProps extends ExtractionSettings {
  onKChange: (value: number) => void;
  onAlgoChange: (value: ExtractionAlgorithm) => void;
  onBrandSafeToggle: (enabled: boolean) => void;
  onRemoveOutliersToggle: (enabled: boolean) => void;
  onOutlierThresholdChange: (value: number) => void;
  onContrastBiasToggle: (enabled: boolean) => void;
  onTargetBackgroundChange: (value: string) => void;
  onMinLightnessChange: (value: number | undefined) => void;
  onMaxLightnessChange: (value: number | undefined) => void;
  onMinSaturationChange: (value: number | undefined) => void;
}

export function ExtractionSettingsPanel({
  extractionK,
  extractionAlgo,
  brandSafeEnabled,
  removeOutliers,
  outlierThreshold,
  contrastBias,
  targetBackground,
  minLightness,
  maxLightness,
  minSaturation,
  onKChange,
  onAlgoChange,
  onBrandSafeToggle,
  onRemoveOutliersToggle,
  onOutlierThresholdChange,
  onContrastBiasToggle,
  onTargetBackgroundChange,
  onMinLightnessChange,
  onMaxLightnessChange,
  onMinSaturationChange,
}: ExtractionSettingsPanelProps) {
  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Colors to extract:
          <input
            type="number"
            value={extractionK}
            onChange={(e) => onKChange(Number(e.target.value))}
            min="2"
            max="20"
            className="pal-extract-field ml-2 w-16 inline"
          />
        </label>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Algorithm:
          <select
            value={extractionAlgo}
            onChange={(e) => onAlgoChange(e.target.value as ExtractionAlgorithm)}
            className="pal-extract-field ml-2 inline"
          >
            <option value="kmeans">K-Means</option>
            <option value="median-cut">Median Cut</option>
          </select>
        </label>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          <input
            type="checkbox"
            checked={brandSafeEnabled}
            onChange={(e) => onBrandSafeToggle(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Brand-Safe Extraction
        </label>

        {brandSafeEnabled && (
          <div className="ml-6 space-y-3 text-sm">
            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={removeOutliers}
                onChange={(e) => onRemoveOutliersToggle(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Remove outlier colors
              {removeOutliers && (
                <input
                  type="number"
                  value={outlierThreshold}
                  onChange={(e) => onOutlierThresholdChange(Number(e.target.value))}
                  min="10"
                  max="100"
                  step="5"
                  className="pal-extract-field ml-2 w-16"
                  title="ΔE threshold for outlier detection"
                />
              )}
            </label>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={contrastBias}
                  onChange={(e) => onContrastBiasToggle(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                Prefer high-contrast colors
              </label>
              {contrastBias && (
                <div className="ml-6 flex items-center gap-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Target background:
                  </span>
                  <input
                    type="color"
                    value={targetBackground}
                    onChange={(e) => onTargetBackgroundChange(e.target.value)}
                    className="w-12 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={targetBackground}
                    onChange={(e) => onTargetBackgroundChange(e.target.value)}
                    className="pal-extract-field w-24 text-xs font-mono"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-gray-600 dark:text-gray-400">
                Min Lightness (0-100):
                <input
                  type="number"
                  value={minLightness ?? ""}
                  onChange={(e) =>
                    onMinLightnessChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  min="0"
                  max="100"
                  placeholder="None"
                  className="pal-extract-field mt-1"
                />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">
                Max Lightness (0-100):
                <input
                  type="number"
                  value={maxLightness ?? ""}
                  onChange={(e) =>
                    onMaxLightnessChange(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  min="0"
                  max="100"
                  placeholder="None"
                  className="pal-extract-field mt-1"
                />
              </label>
            </div>

            <label className="text-xs text-gray-600 dark:text-gray-400">
              Min Saturation (0-100):
              <input
                type="number"
                value={minSaturation ?? ""}
                onChange={(e) =>
                  onMinSaturationChange(
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                min="0"
                max="100"
                placeholder="None"
                className="pal-extract-field mt-1"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
